import cs from 'classnames';
import dayjs from 'dayjs';
import { Divider, Space, Typography } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { AiOutlineWhatsApp } from 'react-icons/ai';
import ItemData from '../Item';
import { formatPhone } from '../../../utils/bookings';
import { IBooking, IBookingEvent } from '../../../interfaces/booking.interface';
import { useWorkspaceStore } from '../../../store/workspace.store';

interface Props {
    showDetail?: boolean;
    type?: 'SUCCESS' | 'FAILED' | 'RESOLVING'
    booking: IBooking;
    bookingEvent: IBookingEvent;
    bookingEventSelected: boolean;
}

const { Text } = Typography;

const AssignStatus = ({
    showDetail = false, type = 'SUCCESS', booking, bookingEvent, bookingEventSelected,
}: Props) => {
    const { t } = useTranslation('bookings', { keyPrefix: 'DETAIL' });

    const workspace = useWorkspaceStore((state) => state.data);
    const date = dayjs(bookingEvent?.createdAt).format('DD MMM HH:mm');

    const phoneFormatted = bookingEvent?.driverMobile
        ? formatPhone(bookingEvent?.driverMobile, bookingEvent?.deliveryProvider)
        : null;

    return (
        !showDetail ? (
            <div className={cs('booking__status', { active: bookingEventSelected })}>
                <Text>
                    <Trans
                        t={t}
                        i18nKey="STORY_LINE.STATUS.ACCEPTED"
                        components={{ b: <b className="booking__status--bold" /> }}
                        values={{
                            deliveryProvider: workspace?.name,
                        }}
                    />
                </Text>
                <Text className="booking__status--time">
                    {date}
                </Text>
            </div>
        ) : (
            <div className="booking__status--detail">
                <Space direction="vertical" size={4}>
                    <Text className="booking__status--detail-title">
                        {t('STORY_LINE.EVENT_DETAILS')}
                    </Text>

                    <Text className={`booking__status--detail-tag ${type}`}>
                        {t(`STORY_LINE.${type}`)}
                    </Text>
                </Space>

                <Text>
                    <Trans
                        t={t}
                        i18nKey="STORY_LINE.STATUS.ACCEPTED_DESCRIPTION"
                        components={{ b: <b className="booking__status--bold" /> }}
                        values={{
                            driverName: bookingEvent.driverName,
                            businessName: booking.businessName,
                            pickupAddress: booking.pickupAddress,
                            deliveryProvider: workspace?.name,
                        }}
                    />
                </Text>

                {
                    booking?.distance && booking?.eta && (
                        <Text>
                            <Trans
                                t={t}
                                i18nKey="STORY_LINE.STATUS.ACCEPTED_HELP"
                                components={{ b: <b className="booking__status--bold" /> }}
                                values={{
                                    driverName: bookingEvent.driverName,
                                    ETADistance: booking?.distance,
                                    ETATime: booking?.eta,
                                }}
                            />
                        </Text>
                    )
                }

                <Text className="booking__status--time">
                    {date}
                </Text>

                <Divider />

                <Text className="booking__status--detail-subtitle">
                    {t('STORY_LINE.INFORMATION')}
                </Text>

                <div className="booking__status--detail-info">
                    <ItemData
                        label={t('DRIVER_NAME')}
                        value={bookingEvent.driverName}
                    />
                    <ItemData
                        label={t('DRIVER_MOBILE')}
                        value={phoneFormatted}
                        actionRequired={phoneFormatted !== null}
                        actionIcon={(
                            <AiOutlineWhatsApp
                                color="var(--color-purple)"
                                size={16}
                                style={{ verticalAlign: 'middle' }}
                            />
                        )}
                        action={phoneFormatted !== null ? () => window.open(`https://wa.me/${phoneFormatted}`, '_blank') : () => {}}
                    />
                </div>

            </div>
        )
    );
};

export default AssignStatus;
